import React, { useState } from 'react';
import { makeStyles } from '@mui/styles';
import { RisksToolbar, RisksTable } from './components';
import CircularProgress from '@mui/material/CircularProgress';
import { Alert } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getRisks } from 'apis/riskAPIs';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import moment from 'moment';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3)
  },
  content: {
    marginTop: theme.spacing(2)
  },
  uiProgress: {
    position: 'fixed',
    zIndex: '1000',
    height: '31px',
    width: '31px',
    left: '50%',
    top: '35%'
  }
}));

const RiskList = () => {
  const classes = useStyles();
  const history = useHistory();
  
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [queryYear, setQueryYear] = useState(currentYear);
  const queryClient = useQueryClient();
  
  const user = useSelector((state) => state.account.user);
  const currencySymbol = (user && user.currencySymbol) ? user.currencySymbol : "$";
  
  const dateTimeFormat = 'DD/MM/YYYY HH:mm:ss';
  // React Query hook for fetching risks
  const {
    data: risks = [],
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['risks', queryYear],
    queryFn: () => getRisks(queryYear),
    retry: 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
    onError: (error) => {
      console.log('Error fetching risks:', error);
      if (error.response && error.response.status === 403) {
        history.push('/sign-in');
      }
    }
  });

  const handleSearch = () => {
    queryClient.invalidateQueries({ queryKey: ['risks'] });
    setQueryYear(selectedYear);
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
  };

  const formatDateTime = (timeString) => {
    if (!timeString) return 'N/A';
    if (typeof timeString === 'object' && '_seconds' in timeString) {
      return moment(new Date(timeString._seconds * 1000)).format(dateTimeFormat);
    }
    if (timeString && typeof timeString === 'number' && timeString.toString().length === 10) {
      return moment(new Date(Number(timeString) * 1000)).format(dateTimeFormat);
    }
    return moment(timeString, dateTimeFormat).format(dateTimeFormat);
  };

  const getTimestamp = (timeString) => {
    if (!timeString) return 0;
    if (typeof timeString === 'object' && '_seconds' in timeString) {
      return timeString._seconds * 1000;
    }
    if (timeString && typeof timeString === 'number' && timeString.toString().length === 10) {
      return Number(timeString) * 1000;
    }
    return moment(timeString, dateTimeFormat).valueOf();
  };

  const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';

    // Define CSV headers
    const headers = ['Risk ID', 'Order Number', 'Risk Type', 'Order Time', 'Restaurant', 'Amount'];
    
    // Sort risks by creation time (descending)
    const sortedRisks = [...data].sort((a, b) => {
      return getTimestamp(b.createdAt) - getTimestamp(a.createdAt);
    });

    // Create CSV rows
    const rows = sortedRisks.map(risk => {
      return [
        risk.id || 'N/A',
        risk.orderNumber || 'N/A',
        risk.riskType || 'N/A',
        formatDateTime(risk.createdAt),
        risk.restaurantName || 'N/A',
        `${currencySymbol}${risk.amount ? Number(risk.amount).toFixed(2) : '0.00'}`
      ];
    });

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => {
        // Escape cells that contain commas, quotes, or newlines
        const cellString = String(cell);
        if (cellString.includes(',') || cellString.includes('"') || cellString.includes('\n')) {
          return `"${cellString.replace(/"/g, '""')}"`;
        }
        return cellString;
      }).join(','))
    ].join('\n');

    return csvContent;
  };

  const handleDownloadCSV = () => {
    if (!risks || risks.length === 0) {
      alert('No data available to download');
      return;
    }

    const csv = convertToCSV(risks);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `risks_${queryYear}_${moment().format('YYYY-MM-DD_HH-mm-ss')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  return (
    <div className={classes.root}>
      <RisksToolbar 
        onSearch={handleSearch}
        year={selectedYear}
        onYearChange={handleYearChange}
        onDownload={handleDownloadCSV}
      />
      <div className={classes.content}>
        {isError && (
          <Alert severity="error" style={{ marginBottom: '16px' }}>
            {error?.response?.data?.message || 'Error in retrieving the data'}
          </Alert>
        )}
        {isLoading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
            <CircularProgress size={150} />
          </div>
        )}
        {!isLoading && (
          <RisksTable risks={risks} />
        )}
      </div>
    </div>
  );
};

export default RiskList;
